import { useState, useEffect } from 'react';
import { Globe, ArrowUpRight, ArrowDownRight, Zap, Target, AlertTriangle, AlertCircle, TrendingUp, Loader2 } from 'lucide-react';
import { apiService } from '../../../services/api';
import { DataFreshnessBanner, PREPARING_POLL, PreparingNotice, useBackgroundRefresh } from './DataFreshness';

// Every field is optional: the AI response can come back partial.
interface MarketIntelligenceData {
  // Set when there is nothing to analyse yet (no business profile / target audiences).
  empty?: boolean;
  message?: string;
  // When the report was made, whether a newer one is being made in the
  // background, and whether the first one is still being prepared.
  updated_at?: string | null;
  refreshing?: boolean;
  preparing?: boolean;
  market_dashboard?: {
    emerging_trends?: string[];
    declining_trends?: string[];
    viral_movement_signals?: string[];
    opportunity_areas?: string[];
  };
  consumer_behavior_shifts?: {
    attention_changes?: { title: string; description: string; trend: string };
    engagement_preference?: { title: string; description: string; trend: string };
    emotional_communication?: { title: string; description: string; trend: string };
  };
  tg_mapping_matrix?: {
    tg_name: string;
    preferred_formats?: string[];
    platform_evolution?: { platform: string; change: string; trend: string }[];
    attention_shifts?: { label: string; value: string; trend: string }[];
  }[];
  opportunity_alerts?: {
    type: string;
    title: string;
    status: string;
  }[];
}

export default function MarketConsumerTGIntelligence() {
  const [data, setData] = useState<MarketIntelligenceData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const userId = localStorage.getItem('user_id') || 'default_user_id';
        const response = await apiService.getMarketIntelligence(userId);
        // Sample data (is_fallback, from an older backend) is never shown as the user's.
        if (response?.is_fallback ?? response?.data?.is_fallback) {
          setError('Live analysis is unavailable right now. Please try again.');
        } else if (response && response.data) {
          setData(response.data);
        } else {
          setError('Invalid data received');
        }
      } catch (err: any) {
        setError(err.message || 'Failed to fetch market intelligence data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [reloadKey]);

  // Re-ask quietly (no spinner) and swap the answer in; refresh=true is the
  // Refresh button (at most once a day, server-side).
  const refetchQuietly = async (refresh = false) => {
    const userId = localStorage.getItem('user_id') || 'default_user_id';
    const response = await apiService.getMarketIntelligence(userId, refresh);
    if (response?.data && !(response.is_fallback ?? response.data.is_fallback)) setData(response.data);
  };
  const freshness = useBackgroundRefresh(data?.refreshing, () => refetchQuietly());
  const preparing = useBackgroundRefresh(data?.preparing, () => refetchQuietly(), PREPARING_POLL);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-32 bg-white rounded-xl shadow-sm border border-slate-200">
        <Loader2 className="w-10 h-10 text-indigo-500 animate-spin mb-4" />
        <h3 className="text-lg font-bold text-slate-800">Generating AI Market Intelligence...</h3>
        <p className="text-slate-500 text-sm mt-2">Analyzing industry trends and audience shifts</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="p-6 bg-red-50 text-red-700 rounded-xl flex items-start gap-4 border border-red-200">
        <AlertCircle className="w-6 h-6 shrink-0 mt-0.5" />
        <div>
          <h3 className="font-bold">Error loading market intelligence</h3>
          <p className="text-sm mt-1">{error}</p>
          <button
            onClick={() => setReloadKey((k) => k + 1)}
            className="mt-3 px-3 py-1.5 bg-red-100 hover:bg-red-200 text-red-800 rounded-lg text-xs font-semibold transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (data.preparing) {
    return <PreparingNotice state={preparing} onRetry={() => setReloadKey((k) => k + 1)} />;
  }

  // Nothing to analyse yet: say what to add instead of showing made-up audiences.
  if (data.empty) {
    return (
      <div className="p-6 bg-white text-slate-700 rounded-xl flex items-start gap-4 border border-slate-200 shadow-sm">
        <Target className="w-6 h-6 shrink-0 mt-0.5 text-indigo-500" />
        <div>
          <h3 className="font-bold text-slate-900">No market intelligence yet</h3>
          <p className="text-sm mt-1 text-slate-500">{data.message || 'Complete your profile to see market intelligence.'}</p>
        </div>
      </div>
    );
  }

  // The response may be partial: guard every nested field so a missing one can't crash the section.
  const market = data.market_dashboard;
  const shifts = data.consumer_behavior_shifts;
  const tgMatrix = data.tg_mapping_matrix ?? [];
  const activeTg = tgMatrix[activeTab];

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-12 animate-in fade-in duration-500">
      <DataFreshnessBanner updatedAt={data.updated_at} state={freshness} onRefresh={() => refetchQuietly(true)} />

      {/* SECTION 1: Market Intelligence Dashboard */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
        <div className="border-b border-slate-100 p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-indigo-50 text-indigo-600 rounded-lg">
              <Globe className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">Market Intelligence Dashboard</h2>
              <p className="text-sm text-slate-500">Aggregated market signals, trend trajectories, and whitespace opportunities</p>
            </div>
          </div>

        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-slate-100">
          <div className="p-6 bg-emerald-50/30">
            <div className="flex items-center gap-2 mb-4">
              <ArrowUpRight className="w-4 h-4 text-emerald-500" />
              <h3 className="font-bold text-slate-900 text-sm">Emerging Trends</h3>
            </div>
            <ul className="space-y-4">
              {(market?.emerging_trends ?? []).map((t, i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 mt-1.5 shrink-0" />
                  <span className="text-[13px] text-slate-600 leading-snug">{t}</span>
                </li>
              ))}
            </ul>
          </div>
          
          <div className="p-6 bg-red-50/30">
            <div className="flex items-center gap-2 mb-4">
              <ArrowDownRight className="w-4 h-4 text-red-500" />
              <h3 className="font-bold text-slate-900 text-sm">Declining Trends</h3>
            </div>
            <ul className="space-y-4">
              {(market?.declining_trends ?? []).map((t, i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-red-400 mt-1.5 shrink-0" />
                  <span className="text-[13px] text-slate-600 leading-snug">{t}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="p-6 bg-amber-50/30">
            <div className="flex items-center gap-2 mb-4">
              <Zap className="w-4 h-4 text-amber-500" />
              <h3 className="font-bold text-slate-900 text-sm">Viral Movement Signals</h3>
            </div>
            <ul className="space-y-4">
              {(market?.viral_movement_signals ?? []).map((t, i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-1.5 shrink-0" />
                  <span className="text-[13px] text-slate-600 leading-snug">{t}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="p-6 bg-purple-50/30">
            <div className="flex items-center gap-2 mb-4">
              <Target className="w-4 h-4 text-purple-500" />
              <h3 className="font-bold text-slate-900 text-sm">Opportunity Areas</h3>
            </div>
            <ul className="space-y-4">
              {(market?.opportunity_areas ?? []).map((t, i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-purple-400 mt-1.5 shrink-0" />
                  <span className="text-[13px] text-slate-600 leading-snug">{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* SECTION 2: Consumer Behavior Shift Reports */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="border-b border-slate-100 p-6 flex items-center gap-3">
          <div className="p-2.5 bg-violet-50 text-violet-600 rounded-lg">
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
            </svg>
          </div>
          <div>
            <h2 className="text-lg font-bold text-slate-900">Consumer Behavior Shift Reports</h2>
            <p className="text-sm text-slate-500">Attention, engagement preference, and emotional communication evolution</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 p-6 gap-6">
          
          <div className="p-5 rounded-xl border border-slate-100 bg-slate-50/50">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-bold text-slate-800">Attention Changes</span>
              {shifts?.attention_changes?.trend === 'down' ? <ArrowDownRight className="w-4 h-4 text-red-500" /> : <ArrowUpRight className="w-4 h-4 text-emerald-500" />}
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-3">{shifts?.attention_changes?.title}</h3>
            <p className="text-[13px] text-slate-500 leading-relaxed">{shifts?.attention_changes?.description}</p>
          </div>

          <div className="p-5 rounded-xl border border-slate-100 bg-slate-50/50">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-bold text-slate-800">Engagement Preference Evolution</span>
              {shifts?.engagement_preference?.trend === 'up' ? <ArrowUpRight className="w-4 h-4 text-emerald-500" /> : <ArrowDownRight className="w-4 h-4 text-red-500" />}
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-3">{shifts?.engagement_preference?.title}</h3>
            <p className="text-[13px] text-slate-500 leading-relaxed">{shifts?.engagement_preference?.description}</p>
          </div>

          <div className="p-5 rounded-xl border border-slate-100 bg-slate-50/50">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-bold text-slate-800">Emotional Communication Trends</span>
              {shifts?.emotional_communication?.trend === 'up' ? <ArrowUpRight className="w-4 h-4 text-emerald-500" /> : <ArrowDownRight className="w-4 h-4 text-red-500" />}
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-3">{shifts?.emotional_communication?.title}</h3>
            <p className="text-[13px] text-slate-500 leading-relaxed">{shifts?.emotional_communication?.description}</p>
          </div>

        </div>
      </div>

      {/* SECTION 3: Static Target Group Matrix */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="p-6 border-b border-slate-100 flex items-center gap-3">
          <div className="p-2.5 bg-fuchsia-50 text-fuchsia-600 rounded-lg">
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
          </div>
          <div>
            <h2 className="text-lg font-bold text-slate-900">Static Target Group (TG) Mapping Matrix</h2>
            <p className="text-sm text-slate-500">Onboarding-defined audience clusters with behavioral evolution tracking</p>
          </div>
        </div>

        <div className="p-6">
          <div className="flex border border-slate-200 rounded-lg p-1 bg-slate-50 mb-6 w-full">
            {tgMatrix.map((tg, idx) => (
              <button
                key={idx}
                onClick={() => setActiveTab(idx)}
                className={`flex-1 py-2 text-sm font-bold rounded-md transition-colors ${activeTab === idx ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}
              >
                {tg.tg_name}
              </button>
            ))}
          </div>

          {activeTg && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              
              <div>
                <h4 className="text-sm font-bold text-slate-800 mb-4 flex items-center gap-2">
                  <span className="w-4 h-4 flex items-center justify-center rounded bg-indigo-100 text-indigo-700 text-[10px]">☰</span>
                  Preferred Formats
                </h4>
                <div className="space-y-2">
                  {(activeTg.preferred_formats ?? []).map((fmt, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 bg-white border border-slate-100 rounded-lg shadow-sm">
                      <div className="w-6 h-6 rounded-full bg-indigo-50 text-indigo-600 flex items-center justify-center text-xs font-bold shrink-0">{i+1}</div>
                      <span className="text-[13px] font-bold text-slate-700">{fmt}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="text-sm font-bold text-slate-800 mb-4 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-blue-500" />
                  Platform Preference Evolution
                </h4>
                <div className="space-y-3">
                  {(activeTg.platform_evolution ?? []).map((plat, i) => (
                    <div key={i} className="flex items-center justify-between pb-3 border-b border-slate-100 last:border-0">
                      <span className="text-[13px] font-bold text-slate-600">{plat.platform}</span>
                      <div className="flex items-center gap-2 text-right">
                        <span className="text-[12px] text-slate-500">{plat.change}</span>
                        {plat.trend === 'up' && <ArrowUpRight className="w-3 h-3 text-emerald-500" />}
                        {plat.trend === 'down' && <ArrowDownRight className="w-3 h-3 text-red-500" />}
                        {plat.trend === 'neutral' && <span className="w-3 text-slate-400">—</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="text-sm font-bold text-slate-800 mb-4 flex items-center gap-2">
                  <svg className="w-4 h-4 text-purple-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                  Attention Span Shifts
                </h4>
                <div className="space-y-3">
                  {(activeTg.attention_shifts ?? []).map((att, i) => (
                    <div key={i} className="flex items-center justify-between pb-3 border-b border-slate-100 last:border-0">
                      <span className="text-[13px] font-medium text-slate-600">{att.label}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-[13px] font-black text-slate-800">{att.value}</span>
                        {att.trend === 'up' && <ArrowUpRight className="w-3 h-3 text-emerald-500" />}
                        {att.trend === 'down' && <ArrowDownRight className="w-3 h-3 text-red-500" />}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

            </div>
          )}

          <div className="mt-8 bg-amber-50 border border-amber-200 text-amber-800 text-xs px-4 py-3 rounded-lg flex items-center gap-2">
            <svg className="w-4 h-4 text-amber-600 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
            <span className="font-semibold">System Restriction:</span> TG Clusters Fixed at Onboarding — Tracking Behavioral Shifts Only. Core persona definitions cannot be modified without re-onboarding workflow.
          </div>
        </div>
      </div>

      {/* SECTION 4: Opportunity Alerts */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2.5 bg-blue-50 text-blue-600 rounded-lg">
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>
          </div>
          <div>
            <h2 className="text-lg font-bold text-slate-900">Opportunity Alerts</h2>
            <p className="text-sm text-slate-500">High-priority whitespace signals requiring immediate strategic consideration</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {(data.opportunity_alerts ?? []).map((alert, i) => (
            <div key={i} className={`p-5 rounded-xl border ${alert.type === 'critical' ? 'border-amber-200 bg-amber-50' : 'border-emerald-200 bg-emerald-50'}`}>
              <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-bold mb-3 ${alert.type === 'critical' ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'}`}>
                {alert.type === 'critical' ? <AlertTriangle className="w-3.5 h-3.5" /> : <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>}
                {alert.status}
              </div>
              <h3 className="text-[15px] font-bold text-slate-900 mb-4 leading-snug">{alert.title}</h3>
              <button className="bg-indigo-600 hover:bg-indigo-700 text-white text-[11px] font-bold px-4 py-2 rounded-lg transition-colors shadow-sm">
                Send to Strategy Module
              </button>
            </div>
          ))}
        </div>
      </div>

    </div>
  );
}